import React, { type ReactNode, useEffect, useState, useCallback, createContext, useContext } from "react";
import {
  useIsAuthenticated,
  useMsalAuthentication,
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";
import { getAccessToken } from "../msal/helpers";
import AppLoading from "../../screens/appLoading/AppLoading";
import { Role, User } from "../../types";
import { getUser } from "../../services/UsersService";
import { useNavigate } from "react-router-dom";
import { LOCAL_USER_KEY, LOCAL_USER_ROLE_KEY } from "../auth.constants";
import { areUrlsEqual } from "../../../utils";

const isEmpty = (obj: any) => [Object, Array].includes((obj || {}).constructor) && !Object.entries((obj || {})).length;

// Export the provider as we need to wrap the entire app with it
interface AuthProviderProps {
  children: ReactNode;
}

type UserProfile = {
  first_name: string;
  last_name: string;
  user: User | null;
};

export const AuthContext = createContext<UserProfile>({
  first_name: "",
  last_name: "",
  user: null,
});

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [dbUser, setDbUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>("");
  const { accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated(accounts[0]);
  const getUserRole = localStorage.getItem(LOCAL_USER_ROLE_KEY);

  const validTokenGetCurrentUser = useCallback(async () => {
    if (isAuthenticated) {
      await getAccessToken();
    }
  }, [isAuthenticated]);

  const names = accounts[0]?.name?.split(" ") || ["", ""];
  const user: UserProfile = {
    first_name: names ? names[0] : "",
    last_name: names && names.length > 1 ? names[names.length - 1] : "",
    user: dbUser,
  };

  const currentLocationUrl = new URL(window.location.href);
  const navigate = useNavigate();

  useEffect(() => {
    const userRole = localStorage.getItem(LOCAL_USER_ROLE_KEY);
    if (isAuthenticated && userRole) {
      const storedRedirect = sessionStorage.getItem("postLoginRedirect");
      if (!storedRedirect) {
        return;
      }

      const storedRedirectUrl = new URL(storedRedirect, window.location.origin);
      if (!areUrlsEqual(currentLocationUrl, storedRedirectUrl)) {
        const pathAndSearch = `${storedRedirectUrl.pathname}${storedRedirectUrl.search}`;
        navigate(pathAndSearch, { replace: true });
      }
      sessionStorage.removeItem("postLoginRedirect");
    }
  }, [isAuthenticated, currentLocationUrl, navigate]);

  // Fetch user data with retry mechanism
  const fetchUserData = useCallback(async () => {
    if (isAuthenticated && accounts.length > 0) {
      const existingUserRole = localStorage.getItem(LOCAL_USER_ROLE_KEY);
      const isNonProducerCached = localStorage.getItem("isNonProducer");

      // If role and NON_PRODUCER info are already cached, skip the API call
      if (existingUserRole && isNonProducerCached !== null) {
        return;
      }

      let attempt = 0;
      const maxRetries = 2;
      const delay = 2000; // 2 seconds

      // Retry function to handle the getUser call with delay
      const retryFetch = async () => {
        while (attempt < maxRetries) {
          try {
            setError(""); // Reset error before each attempt
            const oid = accounts[0].idTokenClaims?.oid;

            const response = await getUser(oid as string);

            if (!response?.bullhorn || isEmpty(response?.bullhorn)) {
              throw Error("No Bullhorn User Found");
            }

            setDbUser(response);

            if (response) {
              localStorage.setItem(LOCAL_USER_KEY, JSON.stringify(response));
            }

            const userRole =
              response?.role?.name === Role.NON_PRODUCER
                ? Role.ACCOUNT_MANAGER // Default to ACCOUNT_MANAGER for NON_PRODUCERS
                : response?.role?.name || "";

            if (userRole) localStorage.setItem(LOCAL_USER_ROLE_KEY, userRole);

            const isNonProducer = response.role.name === Role.NON_PRODUCER;
            localStorage.setItem("isNonProducer", JSON.stringify(isNonProducer));

            return;
          } catch (error) {
            console.error("Attempt failed:", error);
            if (attempt >= maxRetries - 1) {
              if ((error as Error).message === "No Bullhorn User Found") {
                setError("No Bullhorn User Found. Please reach out to sos@alku.com");
              } else {
                setError("Failed to fetch user data");
              }
              break;
            } else {
              attempt++;
              console.log(`Retrying... Attempt ${attempt + 1} of ${maxRetries}`);
              await new Promise((resolve) => setTimeout(resolve, delay)); // Wait for 2 seconds before retrying
            }
          }
        }
      };

      // Call the retry function
      await retryFetch();
    }
  }, [isAuthenticated, accounts, getUserRole]);

  // Fetch the current user bullhornid on initial load
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      await validTokenGetCurrentUser();
      await fetchUserData();
      setLoading(false);
    };

    void initializeAuth();
  }, [validTokenGetCurrentUser, fetchUserData]);

  // We only want to render the underlying app after we
  // assert for the presence of a current user.
  return (
    <>
      <AppAuth />
      {loading || error ? (
        <AppLoading error={error || "" as string} />
      ) : (
        isAuthenticated &&
        getUserRole && ( // User is authenticated
          <AuthenticatedTemplate>
            <AuthContext.Provider value={user}>{children}</AuthContext.Provider>
          </AuthenticatedTemplate>
        )
      )}
      {!isAuthenticated ||
        (getUserRole === null && ( // User is not authenticated
          <UnauthenticatedTemplate>
            <AppLoading error={error || "" as string} />
          </UnauthenticatedTemplate>
        ))}
    </>
  );
}

const AppAuth = (): JSX.Element => {
  const { accounts } = useMsal();

  if (accounts.length === 0 && sessionStorage.getItem("logoutIntent") !== "true") {
    console.log(accounts.length);
    // Save the current path before redirecting
    sessionStorage.setItem("postLoginRedirect", window.location.pathname + window.location.search);
    useMsalAuthentication(InteractionType.Redirect);
  }

  return <></>;
};

// Let's only export the `useAuth` hook instead of the context.
// We only want to use the hook directly and never the context component.
export const useAuth = (): UserProfile => {
  return useContext(AuthContext);
};
